import Vue, { ref } from 'vue'

/**
 * Daily distance for the vehicles table.
 *
 * The value comes from a per-vehicle endpoint, so it is fetched one row at a
 * time, and only for rows the user can actually see (`AsyncCell` asks when the
 * cell scrolls into view). Answers are cached for a while and in-flight
 * requests shared, the same way `plugins/Golia.js` treats reverse geocoding:
 * the table asks again on every polling tick, and a fleet is hundreds of rows.
 */

// A daily total moves slowly; five minutes late is not worth another request.
const CACHE_TTL_MS = 5 * 60 * 1000

// Truck id → distance in km, kept so a row that scrolls back renders at once
const distances = ref({})
// Truck id → when the answer arrived / the request in flight
const fetchedAt = new Map()
const pending = new Map()

function isFresh(truckId) {
    const at = fetchedAt.get(truckId)

    return at != null && Date.now() - at < CACHE_TTL_MS
}

function request(truckId, signal) {
    return Vue.prototype
        .$fetch(`${Vue.prototype.$env.GOLIA_API}/geofleet/trucks/${truckId}/average`, { signal })
        .then((response) => {
            if (!response.ok) throw Error(response.statusText)

            return response.json()
        })
        .then(({ data }) => {
            const distance = data?.distance ?? data?.gps_distance

            fetchedAt.set(truckId, Date.now())
            if (distance != null) Vue.set(distances.value, truckId, distance.toFixed(0))

            return distances.value[truckId]
        })
        .catch((error) => {
            // An aborted request is a row that scrolled away, not a failure:
            // leave the cache empty so the next viewing asks again.
            if (error?.name === 'AbortError') return undefined

            // A real failure is not cached either — the cell shows it and
            // offers a retry, and nothing here would be retried otherwise.
            throw error
        })
        .finally(() => pending.delete(truckId))
}

export function useTruckDailyDistance() {
    /**
     * The distance of one vehicle: from cache when it is still fresh, from the
     * request already in flight when another row asked first, otherwise a new
     * one, cancelled through `signal` if the cell goes away.
     */
    function load(truckId, signal) {
        if (isFresh(truckId)) return Promise.resolve(distances.value[truckId])
        if (pending.has(truckId)) return pending.get(truckId)

        const inFlight = request(truckId, signal)

        pending.set(truckId, inFlight)

        return inFlight
    }

    return { load }
}
